import Vue from 'vue';
import Router from 'vue-router';
import store from '../store';
import { isKycIncomplete } from '../utils/kyc';

import AuthLayout from '../layouts/AuthLayout';
import Login from '../views/Login/index.vue';
import Register from '../views/Register/index.vue';
import ForgotPassword from '../views/ForgotPassword/index.vue';
import DanSetup from '../views/DanSetup/index.vue';
import Kyc from '../views/Kyc/index.vue';
import Document from '../views/Document/index.vue';
import TransactionPage from '../views/TransactionPage';
import MSETransactionPage from '../views/MSETransactionPage/index.vue';
import TradeIpo from '../views/TradeIpo';
import TradeSto from '../views/TradeSto';
import OrderHistory from '../views/OrderHistoryList';
import TradeHistory from '../views/TradeHistoryList';
import NewsList from '../views/NewsList';
import Balance from '../views/Balance';
import ReturnPage from '../views/ReturnPage';
import ProfitPage from '../views/ProfitPage';
import BalanceTDB from '../views/BalanceTDB';
import BalanceMSE from '../views/BalanceMSE';
import Profile from '../views/Profile';
import Statement from '../views/Statement';
import WithdrawalPage from '../views/WithdrawalPage';
import NotificationPage from '../views/Notification/index.vue';


// Suppress NavigationDuplicated errors from router.push / .replace
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch((err) => err);
};
const originalReplace = Router.prototype.replace;
Router.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject);
  return originalReplace.call(this, location).catch((err) => err);
};

Vue.use(Router);

const routes = [
  {
    path: '/user/login',
    name: 'login',
    component: Login,
    meta: { public: true },
  },
  {
    path: '/user/register',
    name: 'register',
    component: Register,
    meta: { public: true },
  },
  {
    path: '/user/forgot-password',
    name: 'forgot-password',
    component: ForgotPassword,
    meta: { public: true },
  },
  {
    path: '/user/dan/setup',
    name: 'dan-setup',
    component: DanSetup,
    meta: { public: true },
  },
  {
    path: '/user/kyc',
    name: 'user-detail',
    component: Kyc,
    meta: { isOther: true, layout: AuthLayout, isKyc: true },
  },
  {
    path: '/user/document',
    name: 'user-document',
    component: Document,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/nominal-transaction',
    name: 'nominal-transaction',
    component: TransactionPage,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/mse-transaction',
    name: 'mse-transaction',
    component: MSETransactionPage,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/trade/ipo',
    name: 'trade-ipo',
    component: TradeIpo,
    meta: { isOther: false, layout: AuthLayout },
  },
  {
    path: '/user/trade/sto',
    name: 'trade-sto',
    component: TradeSto,
    meta: { isOther: false, layout: AuthLayout },
  },
  {
    path: '/user/trade/order-history',
    name: 'order-history',
    component: OrderHistory,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/trade/trade-history',
    name: 'trade-history',
    component: TradeHistory,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/news/list',
    name: 'news-list',
    component: NewsList,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/trade/balance',
    name: 'balance',
    component: Balance,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/return',
    name: 'user.return',
    component: ReturnPage,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/profit',
    name: 'user.profit',
    component: ProfitPage,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/trade/balance-history-tdb',
    name: 'balance-history-tdb',
    component: BalanceTDB,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/trade/balance-history-mse',
    name: 'balance-history-mse',
    component: BalanceMSE,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/trade/profile',
    name: 'profile',
    component: Profile,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/trade/statement',
    name: 'statement',
    component: Statement,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/withdrawal',
    name: 'withdrawal',
    component: WithdrawalPage,
    meta: { isOther: true, layout: AuthLayout },
  },
  {
    path: '/user/notification',
    name: 'notification',
    component: NotificationPage,
    meta: { isOther: true, layout: AuthLayout },
  },
  { path: '/user', redirect: '/user/trade/sto' },
  {
  path: '/',
  beforeEnter: (to, from, next) => {
    if (to.query.access_token_id) {
      next({ path: '/dan/token/result', query: to.query });
    } else {
      next('/user/trade/sto');
    }
  },
  component: { render: () => null }
},
];

const router = new Router({
  mode: 'history',
  linkExactActiveClass: 'open',
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated'];

  // Public routes (login page) — redirect to app if already logged in
  if (to.meta.public) {
    return isAuthenticated ? next({ name: 'trade-sto' }) : next();
  }

  // All other routes require authentication
  if (!isAuthenticated) {
    return next({ name: 'login' });
  }

  if (store.state.common.sbState) store.dispatch('common/toggleSbState');

  if (to.meta.isKyc) return next();

  try {
    if (!store.getters['user/user']) {
      await store.dispatch('user/fetchUser');
    }
  } catch {
    store.dispatch('common/showKycToast');
    return next({ name: 'user-detail' });
  }

  const user      = store.getters['user/user'];
  const kycStatus = store.getters['user/kycStatus'];
  const payment   = store.getters['user/payment'];

  if (isKycIncomplete(user, kycStatus)) {
    store.dispatch('common/showKycToast');
    return next({ name: 'user-detail' });
  }

  const isPaid = payment?.FEE1 === 'paid' && payment?.FEE2 === 'paid';
  if (!isPaid) {
    store.dispatch('common/showPaymentToast');
    return next({ name: 'user-detail' });
  }

  next();
});

export default router;
